/**
 * File Download Utility
 * Handles file downloads for both web and native mobile platforms
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Observable, defer, EMPTY } from 'rxjs';

export class FileDownloadUtil {
  /**
   * Detect if we're on iOS/iPadOS (Safari or native app)
   */
  private static isIOSDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Download a text file (CSV, TXT, etc.)
   * Works on both web and native mobile platforms (iOS/Android)
   */
  static downloadText(content: string, filename: string, mimeType: string = 'text/plain'): Observable<void> {
    return defer(async () => {
      // Native Capacitor apps
      if (Capacitor.isNativePlatform()) {
        await this.downloadNative(content, filename);
        return;
      }

      // iOS/iPadOS web - try Web Share API first
      if (this.isIOSDevice() && navigator.share) {
        const success = await this.tryWebShare(content, filename, mimeType);
        if (success) return;
      }

      // Desktop web or fallback
      this.downloadStandard(content, filename, mimeType);
    });
  }

  /**
   * Download file using native Capacitor APIs (Filesystem + Share)
   */
  private static async downloadNative(content: string, filename: string): Promise<void> {
    const result = await Filesystem.writeFile({
      path: filename,
      data: content,
      directory: Directory.Cache,
      encoding: Encoding.UTF8
    });

    await Share.share({
      title: filename,
      text: filename,
      url: result.uri,
      dialogTitle: 'Save File'
    });
  }

  /**
   * Try to download using Web Share API (for iOS web)
   */
  private static async tryWebShare(content: string, filename: string, mimeType: string): Promise<boolean> {
    try {
      const blob = this.createBlob(content, mimeType);
      const file = new File([blob], filename, { type: blob.type });

      if (navigator.canShare && !navigator.canShare({ files: [file] })) {
        return false;
      }

      await navigator.share({ files: [file], title: 'Export File', text: filename });
      return true;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Download cancelled');
      }
      return false;
    }
  }

  /**
   * Standard blob download for desktop browsers
   */
  private static downloadStandard(content: string, filename: string, mimeType: string): void {
    const blob = this.createBlob(content, mimeType);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Create a blob with the correct MIME type
   */
  private static createBlob(content: string, mimeType: string): Blob {
    return new Blob([content], { type: `${mimeType};charset=utf-8;` });
  }

  /**
   * Download a CSV file
   */
  static downloadCsv(csvContent: string, filename: string): Observable<void> {
    return this.downloadText(csvContent, filename, 'text/csv');
  }
}
